// Pure helpers for remembering a list page's filters (search, dropdowns, dates,
// page, sort...) so "Back" from a View/Edit page returns to the same filtered list.
// No Vue imports here so this can be tested with plain node:
//   node scripts/test-list-state.mjs

export const STORAGE_PREFIX = "listState:";
export const RETURN_TO_KEY = "returnTo";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_STRING = 500;

function isEmpty(value) {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

function sameValue(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a || []) === JSON.stringify(b || []);
  }
  return a === b;
}

// Converts a raw value (string from the URL, or a typed value from storage)
// into the field's type. Returns { ok, value }; ok=false means "drop it".
export function coerce(raw, type) {
  if (Array.isArray(raw) && type !== "array") return { ok: false };
  switch (type) {
    case "string": {
      if (typeof raw === "number") raw = String(raw);
      if (typeof raw !== "string" || raw.length > MAX_STRING) return { ok: false };
      return { ok: true, value: raw };
    }
    case "number": {
      if (typeof raw === "string" && raw.trim() === "") return { ok: false };
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(n) ? { ok: true, value: n } : { ok: false };
    }
    case "boolean": {
      if (raw === true || raw === "1" || raw === "true") return { ok: true, value: true };
      if (raw === false || raw === "0" || raw === "false") return { ok: true, value: false };
      return { ok: false };
    }
    case "date": {
      if (typeof raw !== "string" || !DATE_RE.test(raw)) return { ok: false };
      return Number.isNaN(Date.parse(raw)) ? { ok: false } : { ok: true, value: raw };
    }
    case "array": {
      const list = Array.isArray(raw) ? raw : [raw];
      if (list.some((v) => typeof v !== "string" && typeof v !== "number")) {
        return { ok: false };
      }
      return { ok: true, value: list.map(String) };
    }
    default:
      return { ok: false };
  }
}

function toQueryValue(value, type) {
  if (type === "boolean") return value ? "1" : "0";
  if (type === "array") return value.map(String);
  return String(value);
}

// state -> URL query object. Only fields that differ from their default are
// written, so an unfiltered list keeps a clean URL.
export function serializeState(state, schema) {
  const query = {};
  Object.keys(schema).forEach((field) => {
    const { type, default: def } = schema[field];
    const value = state[field];
    if (isEmpty(value) || sameValue(value, def)) return;
    query[field] = toQueryValue(value, type);
  });
  return query;
}

// URL query -> { state, invalid }. Only fields present in the query appear in
// state; anything that fails type checks is listed in invalid and ignored.
export function parseState(query, schema) {
  const state = {};
  const invalid = [];
  if (!query || typeof query !== "object") return { state, invalid };
  Object.keys(schema).forEach((field) => {
    if (!(field in query)) return;
    const result = coerce(query[field], schema[field].type);
    if (result.ok) state[field] = result.value;
    else invalid.push(field);
  });
  return { state, invalid };
}

export function hasStateInQuery(query, schema) {
  return !!query && Object.keys(schema).some((field) => field in query);
}

// Removes the schema's fields (and returnTo) from a query, keeping anything
// else the page uses (e.g. ?mrf_id from another page).
export function stripStateFromQuery(query, schema) {
  const rest = {};
  Object.keys(query || {}).forEach((key) => {
    if (key in schema || key === RETURN_TO_KEY) return;
    rest[key] = query[key];
  });
  return rest;
}

// Only in-app paths are allowed as Back targets ("/x?y=1"), never
// "//evil.com", "/\evil.com", "https://..." or "javascript:...".
export function isSafeReturnTo(value) {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 2000 &&
    value[0] === "/" &&
    value[1] !== "/" &&
    value[1] !== "\\" &&
    !/[\u0000-\u001f]/.test(value)
  );
}

function storageKey(listPath) {
  return STORAGE_PREFIX + listPath;
}

// sessionStorage can throw (private mode, quota, disabled), so every access is guarded.
export function writeStoredQuery(storage, listPath, query) {
  try {
    if (!storage) return;
    if (!query || Object.keys(query).length === 0) {
      storage.removeItem(storageKey(listPath));
    } else {
      storage.setItem(storageKey(listPath), JSON.stringify(query));
    }
  } catch (e) {
    console.warn("[listState] could not save list filters", e);
  }
}

// Returns the saved query for a list path, or {} if missing/corrupt.
export function readStoredQuery(storage, listPath) {
  let raw;
  try {
    raw = storage ? storage.getItem(storageKey(listPath)) : null;
  } catch (e) {
    console.warn("[listState] could not read list filters", e);
    return {};
  }
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    const valid =
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      Object.values(parsed).every(
        (v) =>
          typeof v === "string" ||
          (Array.isArray(v) && v.every((x) => typeof x === "string"))
      );
    if (valid) return parsed;
  } catch (e) {
    // fall through to discard
  }
  console.warn("[listState] discarding corrupt saved filters for", listPath);
  try {
    storage.removeItem(storageKey(listPath));
  } catch (e) {
    // ignore
  }
  return {};
}

// Called on logout so the next user doesn't inherit filters.
export function clearAllStoredQueries(storage) {
  try {
    if (!storage) return;
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch (e) {
    console.warn("[listState] could not clear list filters", e);
  }
}

// ag-grid sort model <-> "col:asc,col2:desc"
export function serializeSort(sortModel) {
  if (!Array.isArray(sortModel)) return "";
  return sortModel
    .filter((s) => s && s.colId && (s.sort === "asc" || s.sort === "desc"))
    .map((s) => `${s.colId}:${s.sort}`)
    .join(",");
}

export function parseSort(value) {
  if (typeof value !== "string" || value === "") return [];
  return value
    .split(",")
    .map((part) => {
      const idx = part.lastIndexOf(":");
      const colId = part.slice(0, idx);
      const sort = part.slice(idx + 1);
      return colId && (sort === "asc" || sort === "desc") ? { colId, sort } : null;
    })
    .filter(Boolean);
}

// Restored page may no longer exist once data is loaded (records deleted).
export function clampPage(page, totalPages) {
  if (!Number.isFinite(page) || page < 1) return 1;
  if (!Number.isFinite(totalPages) || totalPages < 1) return 1;
  return Math.min(Math.floor(page), totalPages);
}
